import Fuse, { type IFuseOptions, type FuseResult, type FuseResultMatch } from 'fuse.js';
import { getReportDisplayName } from 'app-components/flux/report-configurations/reportConfigurationsConstants';
import type { ReportConfiguration } from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { getLogger } from 'app-components/utils/core';
import { getReportDateRangeLabel } from 'app-components/utils/date';

/**
 * The date range text shown under a saved report's name. Reads exactly like the
 * label in the date range picker the range was chosen in, dynamic ranges included.
 */
export const getSavedReportDateRangeLabel = (config: ReportConfiguration): string =>
  getReportDateRangeLabel(config) ?? '';

const log = getLogger('filterSavedReports');
const MAX_LOGGED_CONFIGS = 50;

const limitStringForLog = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}…`;
};

const toReportConfigLog = (config: ReportConfiguration): Record<string, unknown> => ({
  id: config.id,
  persistentType: config.persistentType,
  source: config.source,
  name: config.name,
  predefinedConfigId: config.predefinedConfigId,
  reportType: config.reportType,
  reportSubType: config.reportSubType,
  rowType: config.rowType,
  columnType: config.columnType,
  dateRange: config.dateRange,
  descriptionLength: config.description?.length ?? 0,
});

/** Human-readable labels for searchable field keys. */
const FIELD_LABELS: Record<string, string> = {
  displayName: 'name',
};

/** A searchable record derived from a ReportConfiguration. */
type SearchableReport = {
  id: string;
  config: ReportConfiguration;
  displayName: string;
};

export type MatchedField = {
  key: string;
  label: string;
  value: string;
  indices: readonly [number, number][];
};

export type SearchResult = {
  config: ReportConfiguration;
  matchedFields: MatchedField[];
};

/** Build a searchable record from a report configuration. */
function toSearchable(config: ReportConfiguration): SearchableReport {
  return {
    id: config.id ?? '',
    config,
    displayName: config.description || getReportDisplayName(config.name),
  };
}

const FUSE_OPTIONS: IFuseOptions<SearchableReport> = {
  keys: [{ name: 'displayName', weight: 1 }],
  includeMatches: true,
  includeScore: true,
  threshold: 0.3,
  ignoreLocation: true,
};

/** Convert a Fuse result into our SearchResult shape. */
function toSearchResult(result: FuseResult<SearchableReport>): SearchResult {
  const matchedFields: MatchedField[] = (result.matches ?? [])
    .filter((m: FuseResultMatch) => m.key && FIELD_LABELS[m.key])
    .map((m: FuseResultMatch) => {
      const key = m.key ?? '';
      return {
        key,
        label: FIELD_LABELS[key],
        value: m.value ?? '',
        indices: m.indices as readonly [number, number][],
      };
    });

  return {
    config: result.item.config,
    matchedFields,
  };
}

/**
 * Filter saved report configurations by a search query using fuzzy matching.
 *
 * Returns SearchResult objects containing the config and metadata about
 * which fields matched (with character indices for highlighting).
 *
 * Returns all configs (with no match metadata) when query is empty.
 */
export function filterSavedReports(query: string, configs: ReportConfiguration[]): SearchResult[] {
  const trimmed = query.trim().toLowerCase();

  log.debug('filterSavedReports called', {
    query: limitStringForLog(trimmed, 200),
    queryLength: trimmed.length,
    configsCount: configs.length,
    configs: configs.slice(0, MAX_LOGGED_CONFIGS).map(toReportConfigLog),
    configsTruncated: configs.length > MAX_LOGGED_CONFIGS,
  });

  if (!trimmed) {
    return configs.map((config) => ({ config, matchedFields: [] }));
  }

  const searchable = configs.map(toSearchable);
  const fuse = new Fuse(searchable, FUSE_OPTIONS);
  return fuse.search(trimmed).map(toSearchResult);
}
